import { cn } from '../../lib/utils';
import React, { useEffect, useRef } from 'react';
import * as THREE from 'three';

export function DottedSurface({ className, theme = 'light', ...props }) {
    const containerRef = useRef(null);
    const sceneRef = useRef(null);

    useEffect(() => {
        if (!containerRef.current) return;

        const container = containerRef.current;
        const parent = container.parentElement;

        // Get parent dimensions for sizing
        const getSize = () => {
            if (parent) {
                const rect = parent.getBoundingClientRect();
                return { width: rect.width, height: rect.height };
            }
            return { width: window.innerWidth, height: window.innerHeight };
        };

        const SEPARATION = 150;
        const AMOUNTX = 40;
        const AMOUNTY = 60;

        // Scene setup
        const scene = new THREE.Scene();
        scene.fog = new THREE.Fog(0xffffff, 2000, 10000);

        const size = getSize();
        const camera = new THREE.PerspectiveCamera(
            60,
            size.width / size.height,
            1,
            10000,
        );
        camera.position.set(0, 355, 1220);

        const renderer = new THREE.WebGLRenderer({
            alpha: true,
            antialias: true,
        });
        renderer.setPixelRatio(window.devicePixelRatio);
        renderer.setSize(size.width, size.height);
        renderer.setClearColor(0x000000, 0);

        // Position canvas at bottom of container
        const canvas = renderer.domElement;
        canvas.style.position = 'absolute';
        canvas.style.bottom = '0';
        canvas.style.left = '0';

        container.appendChild(canvas);

        // Create particles
        const positions = [];
        const colors = [];
        const geometry = new THREE.BufferGeometry();

        for (let ix = 0; ix < AMOUNTX; ix++) {
            for (let iy = 0; iy < AMOUNTY; iy++) {
                const x = ix * SEPARATION - (AMOUNTX * SEPARATION) / 2;
                const y = 0;
                const z = iy * SEPARATION - (AMOUNTY * SEPARATION) / 2;

                positions.push(x, y, z);
                if (theme === 'dark') {
                    colors.push(200, 200, 200);
                } else {
                    colors.push(0, 0, 0);
                }
            }
        }

        geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
        geometry.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));

        const material = new THREE.PointsMaterial({
            size: 8,
            vertexColors: true,
            transparent: true,
            opacity: 0.8,
            sizeAttenuation: true,
        });

        const points = new THREE.Points(geometry, material);
        scene.add(points);

        let count = 0;
        let animationId;

        const animate = () => {
            animationId = requestAnimationFrame(animate);

            const positionAttribute = geometry.attributes.position;
            const positionsArray = positionAttribute.array;

            let i = 0;
            for (let ix = 0; ix < AMOUNTX; ix++) {
                for (let iy = 0; iy < AMOUNTY; iy++) {
                    const index = i * 3;
                    positionsArray[index + 1] =
                        Math.sin((ix + count) * 0.3) * 50 +
                        Math.sin((iy + count) * 0.5) * 50;
                    i++;
                }
            }

            positionAttribute.needsUpdate = true;
            renderer.render(scene, camera);
            count += 0.03;
        };

        const handleResize = () => {
            const newSize = getSize();
            camera.aspect = newSize.width / newSize.height;
            camera.updateProjectionMatrix();
            renderer.setSize(newSize.width, newSize.height);
        };

        window.addEventListener('resize', handleResize);
        animate();

        sceneRef.current = { scene, camera, renderer, animationId };

        return () => {
            window.removeEventListener('resize', handleResize);
            if (sceneRef.current) {
                cancelAnimationFrame(sceneRef.current.animationId);
                sceneRef.current.scene.traverse((obj) => {
                    if (obj instanceof THREE.Points) {
                        obj.geometry.dispose();
                        obj.material.dispose();
                    }
                });
                sceneRef.current.renderer.dispose();
                if (container && canvas.parentNode === container) {
                    container.removeChild(canvas);
                }
            }
        };
    }, [theme]);

    return (
        <div
            ref={containerRef}
            className={cn('pointer-events-none absolute inset-0', className)}
            {...props}
        />
    );
}

export default DottedSurface;
